import { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import HomePage from './pages/HomePage.jsx';
import PlayPage from './pages/PlayPage.jsx';
import MiniGamesPage from './pages/MiniGamesPage.jsx';
import {
  QuizRulesPage,
  ContactPage,
  PrivacyPage,
  TermsPage,
  CookiesPage,
} from './screens/InfoPages.jsx';
import AdLoaderOverlay from './components/AdLoaderOverlay.jsx';
import PWAInstall from './components/PWAInstall.jsx';
import { CoinsProvider } from './hooks/useCoins.jsx';
import { AdProvider } from './context/AdContext.jsx';
import './styles/client.css';

// Re-mount PlayPage when the `?cat=` changes (e.g. picking a recommended quiz)
// so the round fully resets — entry fee, rotated questions, timer and all.
function PlayRoute() {
  const [params] = useSearchParams();
  return <PlayPage key={params.get('cat') || 'default'} />;
}

// Router shell: global ad overlay + the site's routes. Game state and the
// coin economy live inside PlayPage / the CoinsProvider.
function ClientRoutes() {
  const location = useLocation();

  // Safety net: entrance animations (.page-in/.reveal) start at opacity:0 and
  // rely on the animation finishing. If animations never run (disabled, a
  // frozen/hidden tab, etc.), force the content visible so it can NEVER stay
  // hidden. No-op in normal browsers where the animation completes.
  useEffect(() => {
    const t = setTimeout(() => {
      document.querySelectorAll('.page-in, .reveal, .reveal-fade').forEach((el) => {
        if (parseFloat(getComputedStyle(el).opacity) < 0.01 && el.getAnimations) {
          // Force the (finite) entrance animation to its end state — a running
          // animation overrides inline styles, so finish() is what makes it stick.
          el.getAnimations().forEach((a) => {
            try {
              a.finish();
            } catch (e) {
              /* ignore infinite/idle animations */
            }
          });
        }
      });
    }, 1600);
    return () => clearTimeout(t);
  }, [location.key]);

  return (
    <>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/play" element={<PlayRoute />} />
        <Route path="/minigames" element={<MiniGamesPage />} />
        <Route path="/quiz-rules" element={<QuizRulesPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/cookies" element={<CookiesPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      <PWAInstall />
      <AdLoaderOverlay />
    </>
  );
}

// Public client (quizzyloot.com) mounted inside the admin frontend.
// Wrapped in `.ql-app` so all of the client's global styles/fonts are scoped
// here and never bleed into the admin panel. Coins + Ad providers live here.
export default function ClientApp() {
  return (
    <CoinsProvider>
      <AdProvider>
        <div className="ql-app">
          <ClientRoutes />
        </div>
      </AdProvider>
    </CoinsProvider>
  );
}
